import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import PnlChart from 'src/components/PnlChart';
import usePortfolioTradeChartData from 'src/components/PortfolioPage/PortfolioTradeSubPage/hooks/usePortfolioTradeChartData';
import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import { PortfolioTradeChartTabValue } from 'src/components/PortfolioPage/PortfolioTradeSubPage/types';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import { TabOption } from 'src/components/primitives/Tabs/types';
import { cj } from 'src/utils/classNames';
import PortfolioTradeCalendar from 'src/components/PortfolioPage/PortfolioTradeSubPage/PortfolioTradeCalendar/PortfolioTradeCalendar';
import { LoadingChart } from 'src/components/Charts/LoadingChart';

const CHART_TAB_OPTIONS: TabOption<PortfolioTradeChartTabValue>[] = [
	{
		label: 'Cumulative P&L',
		value: 'cumulative',
	},
	{
		label: 'Daily P&L',
		value: 'interval',
	},
	{
		label: 'Calendar',
		value: 'calendar',
	},
];

interface Props {
	period: PortfolioPeriod;
	includeFeesAndFunding: boolean;
	realizedPnlOnly: boolean;
	setIncludeFeesAndFunding: (includeFeesAndFunding: boolean) => void;
	setRealizedPnlOnly: (realizedPnlOnly: boolean) => void;
}

export default function PortfolioTradeSubPageChart({
	period,
	includeFeesAndFunding,
	realizedPnlOnly,
	setIncludeFeesAndFunding,
	setRealizedPnlOnly,
}: Props) {
	const [activeChartTab, setActiveChartTab] =
		useState<PortfolioTradeChartTabValue>('cumulative');

	const [monthIndex, setMonthIndex] = useState(0);
	const lastThreeMonths = useMemo(() => {
		return [
			dayjs(),
			dayjs().subtract(1, 'month'),
			dayjs().subtract(2, 'month'),
		];
	}, []);

	const {
		data: { cumulativePnlData, intervalPnlData, calendarPnlData },
		isLoading,
		isPlaceholderData,
	} = usePortfolioTradeChartData({
		period,
		month: lastThreeMonths[monthIndex],
		includeFeesAndFunding,
		realizedPnlOnly,
		tradeChartTab: activeChartTab,
	});

	return (
		<div className="col-span-12 flex flex-col gap-y-lg rounded-sm bg-container-bg p-md sm:col-span-7 sm:p-lg sm:px-2xl">
			<div className="flex flex-wrap items-center justify-between gap-y-md">
				<Tabs
					size="md"
					variant="secondary"
					className="self-start"
					options={CHART_TAB_OPTIONS}
					activeTab={activeChartTab}
					onTabChange={setActiveChartTab}
				/>
				<div
					className={cj(
						'flex items-center',
						activeChartTab === 'calendar' ? 'justify-between' : 'justify-end'
					)}
				>
					<div className="flex items-center gap-x-lg not-sm:w-full not-sm:justify-between">
						<Checkbox.Default
							id="realized-pnl-only"
							size="sm"
							label="Realized P&L Only"
							checked={realizedPnlOnly}
							onCheckedChange={() => setRealizedPnlOnly(!realizedPnlOnly)}
						/>
						<Checkbox.Default
							id="include-fees-and-funding"
							size="sm"
							label="Include Fees and Funding"
							checked={includeFeesAndFunding}
							onCheckedChange={() =>
								setIncludeFeesAndFunding(!includeFeesAndFunding)
							}
						/>
					</div>
				</div>
			</div>

			{activeChartTab === 'cumulative' && (
				<>
					{isLoading ? (
						<LoadingChart className="grow not-sm:min-h-75" />
					) : (
						<PnlChart
							className={cj(
								'grow not-sm:min-h-75',
								isPlaceholderData && 'animate-pulse'
							)}
							data={cumulativePnlData}
							field="totalPnl"
							label="Cumulative P&L"
							id="cumulative-pnl-chart"
							positive={
								cumulativePnlData[cumulativePnlData.length - 1]?.totalPnl -
									cumulativePnlData[0]?.totalPnl >
								0
							}
							isHourly={period === '24h'}
							isLoading={isPlaceholderData}
							showDriftLogo
						/>
					)}
					<p className="typo-b5 text-text-secondary">
						Cumulative P&L is the sum of your unrealized P&L, realized P&L,
						taker fees, maker rebates, and funding rates. It does not include
						changes to the collateral value.
					</p>
				</>
			)}

			{activeChartTab === 'interval' && (
				<PnlChart
					className={cj(
						'grow not-sm:min-h-75',
						isPlaceholderData && 'animate-pulse'
					)}
					type="bar"
					data={intervalPnlData}
					field="totalPnl"
					label="Daily P&L"
					id="cumulative-pnl-chart"
					isHourly={period === '24h'}
					isLoading={isPlaceholderData}
				/>
			)}

			{activeChartTab === 'calendar' && (
				<PortfolioTradeCalendar
					data={calendarPnlData}
					lastThreeMonths={lastThreeMonths}
					monthIndex={monthIndex}
					setMonthIndex={setMonthIndex}
					isLoading={isPlaceholderData}
				/>
			)}
		</div>
	);
}
